import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HERO_SCENE_EVENT, readHeroScene } from '@/lib/hero-scene'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { navLinks, primaryCta, siteConfig } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [heroNight, setHeroNight] = useState(false)

    useEffect(() => {
        const sync = (): void => setHeroNight(readHeroScene() === 'night')

        sync()
        window.addEventListener(HERO_SCENE_EVENT, sync)

        return () => window.removeEventListener(HERO_SCENE_EVENT, sync)
    }, [])

    useEffect(() => {
        let lastY = window.scrollY

        const onScroll = (): void => {
            const currentY = window.scrollY

            setScrolled(currentY > 12)
            setHidden(currentY > 240 && currentY > lastY + 2)

            if (Math.abs(currentY - lastY) > 2) lastY = currentY
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    const onDark = heroNight && !scrolled && !open

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 px-3 pt-3 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] sm:px-6 sm:pt-4',
                hidden && !open ? '-translate-y-[120%]' : 'translate-y-0'
            )}
        >
            <div className='mx-auto max-w-7xl'>
                <nav
                    aria-label='Main'
                    className={cn(
                        'flex items-center justify-between gap-4 rounded-2xl py-2 pl-4 pr-2 transition-all duration-300 sm:pl-5',
                        scrolled || open
                            ? 'bg-white/80 shadow-[0_18px_40px_-24px_rgba(14,26,18,0.45)] ring-1 ring-black/5 backdrop-blur-xl'
                            : 'bg-transparent shadow-none ring-0'
                    )}
                >
                    <Link
                        href='/'
                        aria-label={`${siteConfig.name} home`}
                        className='rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    >
                        <Logo tone={onDark ? 'light' : 'brand'} />
                    </Link>

                    <span aria-hidden='true' className={cn('hidden h-px flex-1 transition-colors duration-500 lg:block', onDark ? 'bg-white/20' : 'bg-primary-strong/15')} />

                    <ul className='hidden items-center gap-2 md:flex lg:gap-6'>
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    className={cn(
                                        'relative rounded-full px-3 py-2 text-[15px] transition-colors duration-500 after:absolute after:inset-x-3 after:bottom-1 after:h-px after:origin-right after:scale-x-0 after:transition-transform after:duration-500 hover:after:origin-left hover:after:scale-x-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        onDark ? 'text-white/90 after:bg-white' : 'text-primary-strong after:bg-primary-strong'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <span aria-hidden='true' className={cn('hidden h-px flex-1 transition-colors duration-500 lg:block', onDark ? 'bg-white/20' : 'bg-primary-strong/15')} />

                    <div className='flex items-center gap-2'>
                        <ButtonLink
                            href={primaryCta.href}
                            size='sm'
                            className={cn(
                                'hidden h-10 px-5 transition-colors duration-500 sm:inline-flex',
                                onDark ? 'bg-white text-forest hover:bg-white/85' : 'bg-primary-strong hover:bg-forest'
                            )}
                        >
                            {primaryCta.label}
                        </ButtonLink>

                        <button
                            type='button'
                            onClick={() => setOpen((value) => !value)}
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            className={cn(
                                'inline-flex size-10 items-center justify-center rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden',
                                onDark ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-black/[0.05]'
                            )}
                        >
                            {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </div>
                </nav>

                <div
                    id='mobile-menu'
                    hidden={!open}
                    className='mt-2 rounded-2xl bg-white p-2 shadow-[0_24px_48px_-24px_rgba(14,26,18,0.45)] ring-1 ring-black/5 md:hidden'
                >
                    <ul className='flex flex-col'>
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='flex h-12 items-center justify-between rounded-xl px-4 text-base font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {link.label}
                                    <ArrowRight className='size-4 text-muted-foreground' aria-hidden='true' />
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <ButtonLink
                        href={primaryCta.href}
                        onClick={() => setOpen(false)}
                        size='lg'
                        className='mt-2 w-full'
                    >
                        {primaryCta.label}
                    </ButtonLink>
                </div>
            </div>
        </header>
    )
}

export default SiteHeader
